import {PresenzeGiornataPlusRecord} from "@commons/mapping/PresenzeGiornataPlusRecord";
import {PresenzeGiustificativo} from "@commons/mapping/PresenzeGiustificativo";
import {GiustificativoAttestatoINPSRecord} from "@commons/mapping/GiustificativoAttestatoINPSRecord";
import {DateTime} from "luxon";
import {LavoratorePresenzeRecord} from "@commons/mapping/LavoratorePresenzeRecord";
import {GiustificazioneRecord} from "@commons/mapping/GiustificazioneAutoRecord";
import {CachePresenze} from "@commons/mapping/CachePresenze";
import {GiustificativoRecord} from "@commons/mapping/GiustificativoRecord";
import {RiepilogoOrePlus} from "@commons/mapping/RiepilogoOrePlus";
import {GestionePlus} from "@commons/services/presenze_calcolo/GestionePlus";
import {LavoratoreCalcolo} from "@commons/services/presenze_calcolo/Calcolo";

export class AttestatiINPS {
    private readonly lavoratore: Omit<LavoratoreCalcolo, 'dataAssunzione'>;
    private readonly giustificativiAttestati: GiustificativoAttestatoINPSRecord[];
    private readonly giustificativiAttestatiElaborati: string[];
    private readonly cache: CachePresenze;

    constructor(lavoratore: Omit<LavoratoreCalcolo, 'dataAssunzione'>,
                giustificativiAttestati: GiustificativoAttestatoINPSRecord[],
                giustificativiAttestatiElaborati: string[] = [],
                cache: CachePresenze,
    ) {
        this.lavoratore = lavoratore;
        this.giustificativiAttestati = giustificativiAttestati;
        this.giustificativiAttestatiElaborati = giustificativiAttestatiElaborati;
        this.cache = cache;
    }

    public getAttestatiElaborati = () => this.giustificativiAttestatiElaborati;

    public hasAttestato4day = (day: PresenzeGiornataPlusRecord): { hasAttestato: boolean, posCausaleMT: number } => {
        const result = {hasAttestato: false, posCausaleMT: -1};
        //const dtGiornata = DateTime.fromISO(day.dtGiornata!);
        const dtGiornata = day.dtGiornata!;
        // Verifica presenza di giustificativi di malattia da elaborare
        if (this.giustificativiAttestati.length > 0) {
            for (let indCausale = 0; indCausale < this.giustificativiAttestati.length; ++indCausale) {
                const giustificativoAttestato = this.giustificativiAttestati[indCausale];
                // if (!giustificativoAttestato.elaborato && (DateTime.fromISO(giustificativoAttestato.giornoDt!) === dtGiornata)...)
                if (!giustificativoAttestato.elaborato && (giustificativoAttestato.giornoDt! === dtGiornata)
                    && this.giustificativiAttestatiElaborati.findIndex(key => key === GiustificativoAttestatoINPSRecord.getKey(giustificativoAttestato)) === -1 // Elaborato in precedenti calcoli e non ancora salvati e ricaricati
                    //&& !this.giustificativiAttestatiElaborati.includes(giustificativoAttestato)
                ) {
                    result.hasAttestato = true;
                    result.posCausaleMT = PresenzeGiornataPlusRecord.posCausaleHH(day, giustificativoAttestato.codGiuTemp);
                    return result;
                }
            }
        }

        // Verifica presenza di status per ricovero ospedaliero
        // Prelevo proprietà per verifica giustificativi utili all gestione malattia
        const proprietaGestione = LavoratorePresenzeRecord.getProprieta(this.lavoratore, DateTime.fromISO(day.dtGiornata!));
        const elencoStatus = LavoratorePresenzeRecord.getStatus(this.lavoratore, day.dtGiornata);
        for (let indStatus = 0; indStatus < elencoStatus.length; ++indStatus) {
            const status = elencoStatus[indStatus];
            if (status.astensione !== null && GiustificazioneRecord.isValid(status.astensione)) {
                if (status.codice === proprietaGestione!.codStatusRicovero) { // es. 'RICOSP'
                    result.hasAttestato = true;
                    result.posCausaleMT = PresenzeGiornataPlusRecord.posCausaleHH(day, proprietaGestione!.codGiuMalattiaTemp);
                    return result;
                }
            }
        }
        return result;
    };

    public applica = (day: PresenzeGiornataPlusRecord, mancante: number, rilevaMancante: null | (() => number), calcoloMassivo: boolean, mancanteXGiuFS: PresenzeGiustificativo | null) => {
        //const dtGiornata = DateTime.fromISO(day.dtGiornata!);
        const dtGiornata = day.dtGiornata!;
        let decurtaFS = false;
        if (mancanteXGiuFS) {
            mancante = mancante + mancanteXGiuFS.quantita * 60;
            decurtaFS = true;
        }
        const result: { count: number, mancante: number | null, ricalcola: boolean } = {count: 0, mancante: null, ricalcola: false};
        if (this.giustificativiAttestati.length > 0) {
            const newCausaliHH = []; // PresenzeCausali
            for (let indCausale = 0; indCausale < this.giustificativiAttestati.length; ++indCausale) {
                const giustificativoAttestato = this.giustificativiAttestati[indCausale];
                //if (!giustificativoAttestato.elaborato && (DateTime.fromISO(giustificativoAttestato.giornoDt!) === dtGiornata)...)
                if (!giustificativoAttestato.elaborato && (giustificativoAttestato.giornoDt! === dtGiornata)
                    && this.giustificativiAttestatiElaborati.findIndex(key => key === GiustificativoAttestatoINPSRecord.getKey(giustificativoAttestato)) === -1 // Elaborato in precedenti calcoli e non ancora salvati e ricaricati
                    //&& !this.giustificativiAttestatiElaborati.includes(giustificativoAttestato)
                ) {
                    let posCausaleML = PresenzeGiornataPlusRecord.posCausaleHH(day, giustificativoAttestato.codGiu);
                    if (posCausaleML === -1 || !calcoloMassivo) {
                        let addGiu = true;
                        if (posCausaleML >= 0) {
                            addGiu = day.causaliHH[posCausaleML]!.idAttestato === null || day.causaliHH[posCausaleML]!.idAttestato === '';
                        }
                        // Controllo applicabilità da status - INIZIO
                        const giu = this.cache.getGiustificativo(giustificativoAttestato.codGiu)!;
                        const causale = PresenzeGiustificativo.factory({codGiu: giu.codGiu!, tipoGiu: GiustificativoRecord.getTipoGiu(giu)});
                        addGiu = addGiu && true; // N.B. true è il risultato della validazione dello status...
                        // Controllo applicabilità da status - FINE

                        if (addGiu) {
                            if (posCausaleML === -1) {
                                posCausaleML = PresenzeGiornataPlusRecord.posCausaleHH(day, giustificativoAttestato.codGiuTemp);
                            }
                            let mancanteHHCC;
                            if (day.tipoRilevazione === 'MANUALE') {
                                if (posCausaleML >= 0) {
                                    mancanteHHCC = day.causaliHH[posCausaleML]!.quantita; // Se giustificativo presente, utilizzo quantità originale per preservare eventuali ore lavorate!
                                } else {
                                    mancanteHHCC = RiepilogoOrePlus.getTotaleHPrev(day.totali) / 60;
                                }
                            } else {
                                mancanteHHCC = mancante / 60;
                            }
                            PresenzeGiustificativo.setHHCC(causale, mancanteHHCC);

                            if (addGiu) {
                                causale.idAttestato = giustificativoAttestato.idAttestato;
                                newCausaliHH.push(causale);
                                this.giustificativiAttestatiElaborati.push(GiustificativoAttestatoINPSRecord.getKey(giustificativoAttestato));

                                if (decurtaFS && mancanteXGiuFS) {
                                    if (mancanteHHCC >= mancanteXGiuFS.quantita) {
                                        PresenzeGiustificativo.setHHCC(mancanteXGiuFS, 0);
                                    } else {
                                        PresenzeGiustificativo.setHHCC(mancanteXGiuFS, mancanteXGiuFS.quantita - mancanteHHCC);
                                    }
                                }

                            }
                            if (posCausaleML >= 0) {
                                if (day.causaliHH[posCausaleML]!.idWF > 0) {
                                    causale.idWF = day.causaliHH[posCausaleML]!.idWF; // Preservo id WF
                                }
                                day.causaliHH[posCausaleML] = null;
                            }

                            break; // N.B. UN SOLO GIUSTIFICATIVO da ATTESTATO!
                        } else {
                            console.log("Giustificativo da attestato non applicabile!");
                        }
                    }
                }
            }

            result.count = PresenzeGiornataPlusRecord.addCausaliHH(day, newCausaliHH, GestionePlus.maxCausaliOre);
            this.checkOverflowGiu(day, result.count, newCausaliHH);
            if (result.count > 0) {
                if (rilevaMancante !== null) {
                    result.mancante = rilevaMancante();
                    if (result.mancante === 0) {
                        PresenzeGiornataPlusRecord.setAnomalia(day, -3);
                    }
                }
                if (day.arrotondateLocked && (day.tipoRilevazione === 'MANUALE')) {
                    result.ricalcola = true;
                }
            }
        }
        return result;
    };

    /**
     * Controlla se è stato superato il numero massimo di giustificativi inseribili per giornata; in tal caso set anomalia
     * @param day giorno in elaborazione
     * @param count numero giustificativi inseriti
     * @param newCausali array giustificativi da inserire
     */
    private checkOverflowGiu = (day: PresenzeGiornataPlusRecord, count: number, newCausali: PresenzeGiustificativo[]) => {
        if (count < newCausali.length) {
            console.error(LavoratorePresenzeRecord.getDescrizione(this.lavoratore), GestionePlus.formatDate(day.dtGiornata), 'Raggiunto il limite massimo di causali!');
            PresenzeGiornataPlusRecord.setAnomalia(day, 11);
        }
    };
}